'use client';

import { useState } from 'react';
import { 
  X, 
  Clock, 
  User, 
  Link2, 
  FileText, 
  Image, 
  File,
  Check,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import type { 
  DiscoveryGoal, 
  DiscoveryInterview, 
  DiscoveryCompetitor,
  DiscoveryType 
} from '@/hooks/use-discovery';

interface RowDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: DiscoveryType;
  row: DiscoveryGoal | DiscoveryInterview | DiscoveryCompetitor | null;
  projectId: string;
  onEdit: () => void;
  onAttachFile?: (file: File) => Promise<void>;
  onDeleteAttachment?: (attachmentId: string) => Promise<void>;
  isUploading?: boolean;
}

const priorityColors: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  LOW: 'bg-green-100 text-green-700 border-green-200',
};

const categoryColors: Record<string, string> = {
  BUSINESS: 'bg-purple-100 text-purple-700',
  USER: 'bg-blue-100 text-blue-700',
  PRODUCT: 'bg-indigo-100 text-indigo-700',
  TECHNICAL: 'bg-slate-100 text-slate-700',
};

const threatColors: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  LOW: 'bg-green-100 text-green-700',
};

const influenceColors: Record<string, string> = {
  HIGH: 'bg-purple-100 text-purple-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  LOW: 'bg-gray-100 text-gray-700',
};

const competitorCategoryColors: Record<string, string> = {
  DIRECT: 'bg-red-100 text-red-700',
  INDIRECT: 'bg-orange-100 text-orange-700',
  SUBSTITUTE: 'bg-blue-100 text-blue-700',
};

function getFileIcon(fileType: string) {
  if (fileType.startsWith('image/')) return <Image className="w-5 h-5 text-blue-500" />;
  if (fileType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
  return <File className="w-5 h-5 text-gray-500" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RowDetailModal({
  isOpen,
  onClose,
  type,
  row,
  projectId,
  onEdit,
  onAttachFile,
  onDeleteAttachment,
  isUploading = false,
}: RowDetailModalProps) {
  const { toast } = useToast();
  const [linkCopied, setLinkCopied] = useState(false);

  if (!isOpen || !row) return null;

  const handleCopyLink = async () => {
    const baseUrl = window.location.origin;
    const permalink = `${baseUrl}/projects/${projectId}/discovery-${type}?row=${row.id}`;
    
    try {
      await navigator.clipboard.writeText(permalink);
      setLinkCopied(true);
      toast({ title: 'Link copied', description: 'Permalink copied to clipboard' });
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  // Get metadata about generation
  const generatedFrom = (row as DiscoveryInterview | DiscoveryCompetitor).generatedFrom;
  const metadata = (row as any).metadata;
  const isDraft = metadata?.isDraft;

  const renderGoalDetails = () => {
    const goal = row as DiscoveryGoal;
    return (
      <>
        {/* Header badges */}
        <div className="flex items-center gap-2 mb-4">
          {goal.category && (
            <span className={cn("px-2.5 py-1 text-xs font-medium rounded", categoryColors[goal.category])}>
              {goal.category}
            </span>
          )}
          {goal.priority && (
            <span className={cn("px-2.5 py-1 text-xs font-medium rounded border", priorityColors[goal.priority])}>
              {goal.priority}
            </span>
          )}
          {isDraft && (
            <span className="px-2.5 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              AI Draft
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{goal.title}</h2>

        {/* Description */}
        {goal.description && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{goal.description}</p>
          </div>
        )}

        {/* Success Metrics */}
        {goal.successMetrics && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Success Metrics</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{goal.successMetrics}</p>
          </div>
        )}

        {/* Timeline */}
        {goal.timeline && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Timeline</h3>
            <p className="text-gray-700">{goal.timeline}</p>
          </div>
        )}
      </>
    );
  };

  const renderInterviewDetails = () => {
    const interview = row as DiscoveryInterview;
    return (
      <>
        {/* Header badges */}
        <div className="flex items-center gap-2 mb-4">
          {interview.influence && (
            <span className={cn("px-2.5 py-1 text-xs font-medium rounded", influenceColors[interview.influence])}>
              {interview.influence} Influence
            </span>
          )}
          {isDraft && (
            <span className="px-2.5 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              AI Draft
            </span>
          )}
        </div>

        {/* Stakeholder Info */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">{interview.stakeholderName}</h2>
          {(interview.stakeholderTitle || interview.stakeholderOrg) && (
            <p className="text-gray-600 mt-1">
              {interview.stakeholderTitle}
              {interview.stakeholderTitle && interview.stakeholderOrg && ' • '}
              {interview.stakeholderOrg}
            </p>
          )}
          {interview.interviewDate && (
            <p className="text-gray-500 text-sm mt-2 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(interview.interviewDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
        </div>

        {/* Key Insights */}
        {interview.keyInsights && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Key Insights</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{interview.keyInsights}</p>
          </div>
        )}

        {/* Pain Points */}
        {interview.painPoints && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Pain Points</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{interview.painPoints}</p>
          </div>
        )}

        {/* Full Notes */}
        {interview.fullNotes && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Full Notes</h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap text-sm">{interview.fullNotes}</p>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderCompetitorDetails = () => {
    const competitor = row as DiscoveryCompetitor;
    return (
      <>
        {/* Header badges */}
        <div className="flex items-center gap-2 mb-4">
          {competitor.category && (
            <span className={cn("px-2.5 py-1 text-xs font-medium rounded", competitorCategoryColors[competitor.category])}>
              {competitor.category}
            </span>
          )}
          {competitor.threatLevel && (
            <span className={cn("px-2.5 py-1 text-xs font-medium rounded", threatColors[competitor.threatLevel])}>
              {competitor.threatLevel.charAt(0) + competitor.threatLevel.slice(1).toLowerCase()}
            </span>
          )}
          {isDraft && (
            <span className="px-2.5 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              AI Draft
            </span>
          )}
        </div>

        {/* Name */}
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{competitor.name}</h2>

        {/* Market Position */}
        {competitor.marketPosition && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Market Position</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{competitor.marketPosition}</p>
          </div>
        )}

        {/* Strengths & Weaknesses Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {competitor.strengths && (
            <div>
              <h3 className="text-sm font-medium text-green-600 mb-2">Strengths</h3>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-gray-700 whitespace-pre-wrap text-sm">{competitor.strengths}</p>
              </div>
            </div>
          )}
          {competitor.weaknesses && (
            <div>
              <h3 className="text-sm font-medium text-red-600 mb-2">Weaknesses</h3>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-gray-700 whitespace-pre-wrap text-sm">{competitor.weaknesses}</p>
              </div>
            </div>
          )}
        </div>

        {/* Market Share */}
        {competitor.marketShare && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Market Share</h3>
            <p className="text-gray-700">
              {competitor.marketShare}
              {competitor.marketShareSource && (
                <span className="text-gray-500 text-sm ml-2">
                  (Source: {competitor.marketShareSource})
                </span>
              )}
            </p>
          </div>
        )}

        {/* Source Links */}
        {competitor.sourceLinks && (competitor.sourceLinks as string[]).length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Sources</h3>
            <div className="space-y-2">
              {(competitor.sourceLinks as string[]).map((link, idx) => (
                <a
                  key={idx}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-brand-primary hover:underline text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  {link}
                </a>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 capitalize">{type.slice(0, -1)} Details</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Copy Link */}
            <button
              onClick={handleCopyLink}
              className="h-9 px-3 flex items-center gap-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors text-sm"
              title="Copy permalink"
            >
              {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
              {linkCopied ? 'Copied!' : 'Copy Link'}
            </button>
            {/* Edit */}
            <button
              onClick={() => {
                onClose();
                onEdit();
              }}
              className="h-9 px-4 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors text-sm font-medium"
            >
              Edit
            </button>
            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {type === 'goals' && renderGoalDetails()}
          {type === 'interviews' && renderInterviewDetails()}
          {type === 'competitors' && renderCompetitorDetails()}

          {/* Generated From (Provenance) */}
          {generatedFrom && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Generated
              </h3>
              <p className="text-sm text-amber-700">
                <strong>Prompt:</strong> {(generatedFrom as any).prompt}
              </p>
              {(generatedFrom as any).files?.length > 0 && (
                <p className="text-sm text-amber-700 mt-1">
                  <strong>From files:</strong> {(generatedFrom as any).files.join(', ')}
                </p>
              )}
            </div>
          )}


          {/* Metadata */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                Created by {row.createdBy?.name || row.createdBy?.email || 'Unknown'}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(row.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



